import React, {useMemo} from 'react';

import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {
  DeepResearchReportMessageContents,
  SaveDeepResearchReportButton,
} from 'llm/deep-research/messages/DeepResearchReport';
import LLMFeedbackChatToolbar from 'llm/feedback/LLMFeedbackChatToolbar';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import LLMMarkdownSection from 'llm/markdown/LLMMarkdownSection';
import {assertNever} from 'util/Types';

import {ChatMessageBubble, ChatMessageToolbar} from './ChatMessageBubble';
import {
  ClientStreamingAssistantMessage,
  DisplayMessageChatEventNode,
  DisplayMessageAssistantResponseType,
} from './MessageTypes';
import {getDisplayMessageAssistantResponseType} from './MessageUtils';

type AssistantMessage = DisplayMessageChatEventNode | ClientStreamingAssistantMessage;

export interface ChatAssistantMessageBubbleProps {
  event: AssistantMessage;
  progressData?: ProgressData;
  /** If true, hides interactive components with side-effects. */
  readonly?: boolean;
  chatSessionId: string;
}

function getToolbarActions(
  event: AssistantMessage,
  {readonly, chatSessionId}: {readonly: boolean; chatSessionId: string}
): React.ReactNode {
  const responseType = getDisplayMessageAssistantResponseType(event);
  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPORT: {
      const messageText = getMarkdown(event);
      // TODO(BNCH-130635): "DeepResearchReport" is a misnomer, as other agents can produce reports, too.
      return !readonly ? (
        <SaveDeepResearchReportButton markdownReport={messageText} chatSessionId={chatSessionId} />
      ) : null;
    }
    case DisplayMessageAssistantResponseType.REPLY:
      return null;
    default:
      assertNever(responseType);
      return null;
  }
}
function getMarkdown(event: AssistantMessage): string {
  return event.messageBlocks
    .flatMap((block) => {
      switch (block.__typename) {
        case 'DisplayTextBlockNode':
          return block.text;
        case 'DisplayFileBlockNode':
          // Ignore file blocks for now. Deep Research returns file blocks when the image generation tool is called.
          // These are rendered inline using bnch_file tags, so we shouldn't render them here.
          return [];
        default:
          assertNever(block);
          return [];
      }
    })
    .join('\n');
}

export default function ChatAssistantMessageBubble({
  event,
  progressData,
  readonly = false,
  chatSessionId,
}: ChatAssistantMessageBubbleProps): JSX.Element {
  // Implicitly, message blocks are markdown. In the future, that might not be the case, and the block
  // itself will indicate its type.
  const markdown = useMemo(() => getMarkdown(event), [event]);

  const toolbar = useMemo(
    () => (
      <ChatMessageToolbar
        left={
          // Only render the feedback toolbar for persisted messages.
          !readonly && event.__typename === 'DisplayMessageChatEventNode' ? (
            <LLMFeedbackChatToolbar
              chatEventId={event.id}
              chatEventCreatedAt={event.createdAt}
              agentType={event.agentType}
            />
          ) : (
            <div>
              {event.__typename === 'ClientStreamingAssistantMessage' && event.progressMessage ? (
                <LLMLoadingMessage text={event.progressMessage} />
              ) : null}
            </div>
          )
        }
        right={getToolbarActions(event, {readonly, chatSessionId})}
      />
    ),
    [event, readonly, chatSessionId]
  );

  const responseType = getDisplayMessageAssistantResponseType(event);

  switch (responseType) {
    case DisplayMessageAssistantResponseType.REPLY:
      return (
        <ChatMessageBubble variant="reply" toolbar={toolbar}>
          <LLMMarkdownSection markdown={markdown} chatSessionId={chatSessionId} />
        </ChatMessageBubble>
      );
    case DisplayMessageAssistantResponseType.REPORT:
      return (
        <ChatMessageBubble variant="report" toolbar={toolbar}>
          {/* TODO(BNCH-130635): "DeepResearch" is a misnomer here, as we want non-Deep-Research
              agents to share the same report-with-citations experience. There is nothing that makes
              this renderer specific to one agent type. */}
          <DeepResearchReportMessageContents
            report={markdown}
            progressData={progressData}
            readonly={readonly}
            chatSessionId={chatSessionId}
          />
        </ChatMessageBubble>
      );
    default:
      assertNever(responseType);
      // NB: This should never be exercised as responseType is generated on the client.
      // If we move this to be a persisted field, this fallback may be exercised.
      return (
        <ChatMessageBubble variant="reply" toolbar={toolbar}>
          <LLMMarkdownSection markdown={markdown} chatSessionId={chatSessionId} />
        </ChatMessageBubble>
      );
  }
}
